import { useEffect, useState } from 'react';
import { Alert, Button, Group, Stack, Text, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';

import { FormModal } from '../ui/FormModal';
import { statusPalette } from '../../theme/colors';
import { getApiErrorMessage, getApiViolations } from '../../utils/errorHandling';
import { mapViolations } from '../../utils/mapViolations';

interface ReturnForRevisionModalProps<T> {
  opened: boolean;
  onClose: () => void;
  /** Der POST an das Backend; wirft bei 422/409/403. */
  onSubmit: (reason: string) => Promise<T>;
  onReturned: (updated: T) => void;
}

/**
 * Dialog "In Ueberarbeitung geben" (Phase 43, D-06). Die Begruendung ist Pflicht, aber
 * ausschliesslich das Backend entscheidet: ein leeres Feld geht roh hin, die 422-Meldung
 * erscheint am Textfeld. Keine Frontend-Validierung (validation-approach).
 */
export function ReturnForRevisionModal<T>({
  opened,
  onClose,
  onSubmit,
  onReturned,
}: ReturnForRevisionModalProps<T>) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Controlled: der Dialog ist klein, und der Reset beim Oeffnen muss das Feld sofort leeren.
  const form = useForm({
    mode: 'controlled',
    initialValues: { reason: '' },
  });

  useEffect(() => {
    if (opened) {
      form.reset();
      setError(null);
    }
    // form ist ueber die Lebenszeit der Komponente stabil; nur das Oeffnen soll zuruecksetzen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  async function handleSubmit(values: { reason: string }) {
    setError(null);
    setSaving(true);
    try {
      // Roh, das Backend trimmt und prueft.
      const updated = await onSubmit(values.reason);
      onReturned(updated);
    } catch (err) {
      form.setErrors(mapViolations(getApiViolations(err)));
      setError(getApiErrorMessage(err, 'Fehler beim Zurückgeben der Meldung'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormModal opened={opened} onClose={onClose} title="In Überarbeitung geben">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        {/* Flex-Spalte: Aktionsknoepfe bleiben unter dem wachsenden Textfeld sichtbar; das
            Textfeld waechst nur begrenzt (maxRows), danach scrollt es intern. */}
        <Stack>
          <Text size="sm">
            Die Meldung geht an das Werk zurück und kann dort wieder bearbeitet werden.
          </Text>
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {error}
            </Alert>
          )}
          <Textarea
            label="Begründung"
            autosize
            minRows={6}
            maxRows={12}
            data-autofocus
            {...form.getInputProps('reason')}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              loading={saving}
              styles={{ root: { backgroundColor: statusPalette.revision.fg } }}
            >
              In Überarbeitung geben
            </Button>
          </Group>
        </Stack>
      </form>
    </FormModal>
  );
}
